import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import { useCasesQueryKeys } from "./use-cases-query-keys"
import {
  addParticipant,
  listParticipants,
  removeParticipant,
  updateParticipantRole,
} from "../api/use-cases-api"

export function useUseCaseParticipants(useCaseId: string) {
  return useQuery({
    queryKey: useCasesQueryKeys.participants(useCaseId),
    queryFn: () => listParticipants(useCaseId),
    enabled: !!useCaseId,
  })
}

export function useAddUseCaseParticipant() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      useCaseId,
      participant,
    }: {
      useCaseId: string
      participant: { oid: string; displayName: string; type: "user" | "group"; role: string }
    }) => addParticipant(useCaseId, participant),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: useCasesQueryKeys.participants(variables.useCaseId),
      })
      void queryClient.invalidateQueries({ queryKey: useCasesQueryKeys.list() })
    },
  })
}

export function useUpdateUseCaseParticipantRole() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      useCaseId,
      participantOid,
      role,
    }: {
      useCaseId: string
      participantOid: string
      role: string
    }) => updateParticipantRole(useCaseId, participantOid, role),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: useCasesQueryKeys.participants(variables.useCaseId),
      })
    },
  })
}

export function useRemoveUseCaseParticipant() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ useCaseId, participantOid }: { useCaseId: string; participantOid: string }) =>
      removeParticipant(useCaseId, participantOid),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: useCasesQueryKeys.participants(variables.useCaseId),
      })
      void queryClient.invalidateQueries({ queryKey: useCasesQueryKeys.list() })
    },
  })
}
